import React, { useEffect, useRef } from 'react';
import { useQueryLoader } from 'react-relay';
import { isFedRamp } from '@atlassian/atl-context';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
} from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { INSIGHTS_PACKAGE } from '@atlassian/jira-insights-common-constants/src/common/constants/package.tsx';
import { UserPropertiesContainer } from '@atlassian/jira-insights-common/src/services/user-properties/index.tsx';
import {
	NEXT_BEST_TASK_ANALYTICS,
	NEXT_BEST_TASK_USER_PROPERTIES_SCOPE,
	PACKAGE_NAME,
} from '@atlassian/jira-insights-next-best-task/src/common/constants.tsx';
import { useNextBestTaskActions } from '@atlassian/jira-insights-next-best-task/src/services/next-best-task-container/index.tsx';
import { useContextAri } from '@atlassian/jira-insights-next-best-task/src/services/use-context-ari/index.tsx';
import { useNextBestTaskPanel } from '@atlassian/jira-insights-next-best-task/src/services/use-next-best-task-panel/index.tsx';
import {
	SafeNBTDataLoaderForOnlyCurrentUserSelected,
	SafeNBTDataLoaderForOnlyNBTEnabledAvatarSelected,
} from '@atlassian/jira-insights-next-best-task/src/ui/nbt-data-loader/index.tsx';
import { AsyncNBTPanel } from '@atlassian/jira-insights-next-best-task/src/ui/panel/async.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import NBTgithubOnboardingQuery, {
	type footer_NBTPanelGithubOnboardingDetails_Query,
} from '@atlassian/jira-relay/src/__generated__/footer_NBTPanelGithubOnboardingDetails_Query.graphql';
import { nextBestTaskFeatureResource } from '@atlassian/jira-router-resources-next-best-task/src/services/index.tsx';
import { isClientSideThirdPartyAppError } from '@atlassian/jira-software-sla-tracker/src/services/response-to-sla/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useBoardSelector } from '../../../state/index.tsx';
import { rapidViewIdSelector } from '../../../state/selectors/software/software-selectors.tsx';
import {
	isFilterOnCurrentUserOnly,
	isFilterOnCurrentUserOrUnassignedOnly,
} from '../../../state/selectors/work/work-selectors.tsx';
import { useIsCMPBoard, useIsJSWBoard } from '../../../state/state-hooks/capabilities/index.tsx';

export const ConnectedNextBestTaskPanel = () => {
	const isCMPBoard = useIsCMPBoard();
	const boardId = useBoardSelector(rapidViewIdSelector);

	const onStart = useExperienceStart({
		experience: NEXT_BEST_TASK_ANALYTICS,
		analyticsSource: INSIGHTS_PACKAGE,
	});

	useEffect(() => {
		onStart();
	}, [onStart]);

	return <AsyncNBTPanel isSimplifiedProject={!isCMPBoard} boardId={boardId} />;
};

export const NextBestTaskPanelInner = () => {
	const accountId = useAccountId();
	if (!accountId) throw new Error('accountId could not be found');

	return (
		<UserPropertiesContainer scope={NEXT_BEST_TASK_USER_PROPERTIES_SCOPE} accountId={accountId}>
			<ConnectedNextBestTaskPanel />
		</UserPropertiesContainer>
	);
};

export const NextBestTaskPanel = () => {
	const { data, loading } = useResource(nextBestTaskFeatureResource);
	const isOnlyCurrentUserSelected = useBoardSelector(isFilterOnCurrentUserOnly) || false;
	const isOnlyCurrentUserOrUnassignedSelected =
		useBoardSelector(isFilterOnCurrentUserOrUnassignedOnly) || false;
	const cloudId = useCloudId();
	const { projectAri } = useContextAri();
	const mountedNBTRef = useRef<boolean>(false);

	const isJSWBoard = useIsJSWBoard();

	const [queryRef, loadGitHubOnboardingQuery] =
		useQueryLoader<footer_NBTPanelGithubOnboardingDetails_Query>(NBTgithubOnboardingQuery);

	const { setQueryGithubOnboardingRef } = useNextBestTaskActions();

	const isOptedIn = !!data?.hasUserOptedIn;

	const isNBTEnabled = !loading && data != null && isOptedIn && isJSWBoard;

	const { isOpen } = useNextBestTaskPanel();

	if (
		isNBTEnabled && expVal('jira-work-suggestions-unassigned-avatar-exp', 'isEnabled', false)
			? isOnlyCurrentUserOrUnassignedSelected
			: isOnlyCurrentUserSelected
	) {
		mountedNBTRef.current = true;
	}

	useEffect(() => {
		if (isOpen && queryRef != null) {
			setQueryGithubOnboardingRef(queryRef);
		}
	}, [queryRef, setQueryGithubOnboardingRef, isOpen]);

	useEffect(() => {
		if (isNBTEnabled && isOpen && projectAri && queryRef == null) {
			loadGitHubOnboardingQuery({
				cloudId,
				projectAri,
			});
		}
	}, [isNBTEnabled, loadGitHubOnboardingQuery, cloudId, isOpen, queryRef, projectAri]);

	const addAnalyticAttributes = (error: Error) => ({
		isClientFetchError:
			isClientFetchError(error) ||
			isClientSideThirdPartyAppError(error) ||
			isHttpClientErrorResponse(error),
	});

	return isNBTEnabled ? (
		<JSErrorBoundary
			id="jira.insights.next-best-task-panel"
			packageName={PACKAGE_NAME}
			fallback="flag"
			attributes={addAnalyticAttributes}
			teamName="nova"
		>
			{expVal('jira-work-suggestions-unassigned-avatar-exp', 'isEnabled', false) ? (
				<SafeNBTDataLoaderForOnlyNBTEnabledAvatarSelected
					isOnlyNBTEnabledAvatarSelected={isOnlyCurrentUserOrUnassignedSelected}
				/>
			) : (
				<SafeNBTDataLoaderForOnlyCurrentUserSelected
					isOnlyCurrentUserSelected={isOnlyCurrentUserSelected}
				/>
			)}
			{mountedNBTRef.current && (
				<Placeholder fallback={null} name="next-best-task-panel">
					{!isFedRamp() && <NextBestTaskPanelInner />}
				</Placeholder>
			)}
		</JSErrorBoundary>
	) : null;
};
